import React, { createContext, useContext, useEffect, useState } from 'react';
import { useSimplifiedUserData } from '@/providers/SimplifiedUserDataProvider';
import { useToast } from '@/hooks/use-toast';
import { TypeItem, fetchTypes, getCachedTypes, typesStore } from '@/lib/stores/typesStore';

interface TypesDataContextType {
  types: TypeItem[];
  isLoading: boolean;
  error: string | null;
  refreshTypes: () => Promise<void>;
}

const TypesDataContext = createContext<TypesDataContextType | undefined>(undefined);

export function TypesDataProvider({ children }: { children: React.ReactNode }) {
  const { selectedGame } = useSimplifiedUserData();
  const { toast } = useToast();
  const [types, setTypes] = useState<TypeItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadTypes = async () => {
    if (!selectedGame) {
      setTypes([]);
      setError(null);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // First try to get cached data
      const cachedTypes = getCachedTypes(selectedGame.id);
      if (cachedTypes.length > 0) {
        setTypes(cachedTypes);
        setIsLoading(false);
        return;
      }

      // If no cached data, fetch from API
      const fetchedTypes = await fetchTypes(selectedGame.id, toast);
      setTypes(fetchedTypes);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load types';
      setError(message);
      console.error('Error loading types:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const refreshTypes = async () => {
    await loadTypes();
  };

  // Load types when selected game changes
  useEffect(() => {
    loadTypes();
  }, [selectedGame?.id]);

  // Subscribe to store changes for real-time updates
  useEffect(() => {
    if (!selectedGame?.id) return;

    const unsubscribe = typesStore.subscribe(() => {
      // Get the latest data from the store and filter by current game
      const allTypes = typesStore.getData();
      const gameTypes = allTypes.filter(type => type.game_id === selectedGame.id);
      setTypes(gameTypes);
    });

    return () => unsubscribe();
  }, [selectedGame?.id]);

  const value: TypesDataContextType = {
    types,
    isLoading,
    error,
    refreshTypes,
  };

  return (
    <TypesDataContext.Provider value={value}>
      {children}
    </TypesDataContext.Provider>
  );
}

export function useTypesDataContext() {
  const context = useContext(TypesDataContext);
  if (context === undefined) {
    throw new Error('useTypesDataContext must be used within a TypesDataProvider');
  }
  return context;
} 